/**
 * hudOcclusionZones — tiny screen-space rectangle registry for HUD surfaces
 * that can occlude world-layer content (first consumer: the target panel's
 * frame, W1a 2026-09-28).
 *
 * ## Why a store
 * The portal-prompt clamp (`PixiNameTag.updateClampedPosition`) runs on every
 * Pixi tick. Calling `getBoundingClientRect()` there would force layout each
 * frame, so the owning DOM component publishes its measured rect on LAYOUT
 * changes (ResizeObserver + its layout hook's measured values) and the render
 * path reads this cached snapshot instead. Reads are DOM-free and (while
 * nothing changes) allocation-free.
 *
 * ## Coordinate space
 * Rects are SCREEN-SPACE VIEWPORT LOGICAL CSS PIXELS with a top-left origin —
 * exactly what `element.getBoundingClientRect()` returns for `position: fixed`
 * HUD surfaces. `computePortalPromptPlacement` produces the same space: the
 * Pixi overlay canvas is mounted at viewport (0, 0) sized to the full viewport
 * (`PixiOverlayHost.initialize`: host at top/left 0 with 100%/100% bounds,
 * `app.init({ resizeTo: window })`, logical `app.screen` pixels). No
 * conversion is required, and publishers must NOT pre-scale for
 * devicePixelRatio.
 */

export interface HudOcclusionRect {
  /** Left edge in viewport logical CSS pixels. */
  left: number;
  /** Top edge in viewport logical CSS pixels. */
  top: number;
  /** Right edge in viewport logical CSS pixels. */
  right: number;
  /** Bottom edge in viewport logical CSS pixels. */
  bottom: number;
}

/** Registry id for the TargetInfoPanel's measured frame. */
export const HUD_OCCLUSION_TARGET_PANEL = 'target-info-panel';

const zones = new Map<string, HudOcclusionRect>();
let snapshot: ReadonlyArray<HudOcclusionRect> = Object.freeze([]);
let snapshotStale = false;

function isUsableRect(rect: HudOcclusionRect | null | undefined): rect is HudOcclusionRect {
  return (
    !!rect &&
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.right) &&
    Number.isFinite(rect.bottom) &&
    rect.right > rect.left &&
    rect.bottom > rect.top
  );
}

function sameRect(a: HudOcclusionRect, b: HudOcclusionRect): boolean {
  return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}

/**
 * Publish (or replace) the rect for a named zone. Degenerate or non-finite
 * rects clear the zone instead of poisoning placement math with NaN.
 * Re-publishing an identical rect is a no-op so per-tick readers can keep the
 * same cached array reference.
 */
export function setHudOcclusionZone(
  id: string,
  rect: HudOcclusionRect | null | undefined,
): void {
  if (!isUsableRect(rect)) {
    clearHudOcclusionZone(id);
    return;
  }
  const current = zones.get(id);
  if (current && sameRect(current, rect)) return;
  zones.set(id, { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom });
  snapshotStale = true;
}

/** Remove a named zone (panel hidden/unmounted). No-op when absent. */
export function clearHudOcclusionZone(id: string): void {
  if (zones.delete(id)) snapshotStale = true;
}

/**
 * Current zones as a frozen array of frozen rects, cached between changes.
 * Safe (and cheap) to call from the per-frame render path.
 */
export function getHudOcclusionRects(): ReadonlyArray<HudOcclusionRect> {
  if (snapshotStale) {
    snapshot = Object.freeze(
      Array.from(zones.values(), (rect) => Object.freeze({ ...rect })),
    );
    snapshotStale = false;
  }
  return snapshot;
}

/** Test/teardown helper — drops every zone and the cached snapshot. */
export function resetHudOcclusionZones(): void {
  zones.clear();
  snapshot = Object.freeze([]);
  snapshotStale = false;
}
